import { useEffect } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { type Toast, useToastStore } from '../../store/toastStore';

const VARIANT_STYLES: Record<Toast['variant'], { bg: string; ring: string; text: string }> = {
  success: { bg: 'bg-wca-surface', ring: 'ring-wca-gold', text: 'text-wca-black' },
  error: { bg: 'bg-wca-red', ring: 'ring-wca-red-dark', text: 'text-white' },
  info: { bg: 'bg-wca-surface', ring: 'ring-wca-gray-light', text: 'text-wca-black' },
};

/**
 * Rendered once at the Layout level. Bottom-right on desktop, bottom-
 * centered on mobile so it's reachable with a thumb and doesn't cover
 * the floating cart button.
 */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4 sm:bottom-6 sm:left-auto sm:right-6 sm:items-end"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastCard({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);
  const styles = VARIANT_STYLES[toast.variant];

  useEffect(() => {
    const id = window.setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(id);
  }, [toast.id, toast.durationMs, dismiss]);

  const Icon =
    toast.variant === 'success' ? CheckCircle2 : toast.variant === 'error' ? AlertCircle : Info;

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto w-full max-w-sm rounded-card shadow-lg ring-1 cm-toast-in ${styles.ring} ${styles.bg} ${styles.text}`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <Icon
          size={20}
          aria-hidden="true"
          className={`shrink-0 mt-0.5 ${toast.variant === 'success' ? 'text-wca-gold' : ''}`}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">{toast.title}</p>
          {toast.description !== undefined && (
            <p className="mt-0.5 text-xs opacity-90 leading-snug">{toast.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => dismiss(toast.id)}
          aria-label="Dismiss"
          className="-mr-1 -mt-1 p-1 opacity-70 hover:opacity-100"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
